"use client"
import {
  Box,
  CardHeader,
  Skeleton,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@mui/material"
import {
  Chip,
  ContentTable,
  HighlightedText,
  Link,
} from "@tc/ui-dfe/components"
import { usePrefetchRequest, useViewAllRequest } from "@tc/ui-dfe/hooks"
import { ExportRequestIcon } from "@tc/ui-dfe/icons"
import { ExportStatus } from "@tc/ui-dfe/query"
import { toDate, toPacificAucklandDateFormat } from "@tc/ui-dfe/utils/convertor"
import { ContentTableColumn, DataModel } from "@tc/ui-dfe/utils/types"
import { Permission } from "@tc/ui-permission/components"
import { usePermission } from "@tc/ui-permission/hooks"
import { FEATURE_FLAGS, ROLE } from "@tc/ui-permission/utils"
import { get } from "lodash"
import { EmptyPage } from "../ViewAllTransfers/EmptyTransferPage"
import { ExportRequestData } from "./ExportRequestData"
import { Filters } from "./Filters"
import { getOcaColumns } from "./OcaColumns"
import { Certificate, RequestInfo } from "./RequestTableColumns"
import { GetSwitchProps } from "./types"

interface RequestCellProps {
  id: string
  country_name: string
  request: string
  certificate_number: string
  status: string
}

const MobileRequestCell = (props: RequestCellProps) => {
  const { id, country_name, request, certificate_number, status } = props
  return (
    <Stack key={id + "request"} flexGrow={1} spacing={0.5}>
      <CardHeader
        disableTypography
        avatar={<ExportRequestIcon />}
        title={
          <Typography
            variant="h6"
            color="forest"
            gutterBottom
            title={request}
            aria-label={`Request: ${request}`}
            sx={{
              wordBreak: "break-word",
              whiteSpace: "normal",
            }}
          >
            {request}
          </Typography>
        }
        subheader={
          <Stack>
            <Stack
              gap={1}
              sx={{
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
              }}
            >
              <Box>
                <Chip
                  label={status}
                  size="small"
                  aria-label={`Status: ${status}`}
                  sx={{ textTransform: "capitalize" }}
                />
              </Box>

              <Typography
                noWrap
                component={"span"}
                title={country_name}
                aria-label={`Destination market: ${country_name}`}
                sx={{ maxWidth: 190 }}
              >
                {country_name}
              </Typography>
            </Stack>
            <Certificate id={id} certificate_number={certificate_number} />
          </Stack>
        }
        sx={{
          alignItems: "flex-start",
          py: 0,
          pl: 0,
          "& .MuiCardHeader-action": {
            mt: 0,
          },
        }}
      />
    </Stack>
  )
}

const getThreePointZeroSwitch = ({
  status,
  index,
  request,
  requestId,
  isMobile = false,
  country_name,
  certificate_number,
  statusLabel,
  queryText,
  submissionLayout,
  onPrefetch,
  isNonConsignmentCertificate,
}: GetSwitchProps) => {
  // Only prefetch for configurable routes
  const isConfigurable = submissionLayout && submissionLayout !== "CUSTOM"

  const content = (href: string) => (
    <Link
      href={href}
      custom
      prefetch={false}
      onPrefetch={isConfigurable ? onPrefetch : undefined}
    >
      {isMobile ? (
        <MobileRequestCell
          id={requestId}
          country_name={country_name || ""}
          request={request}
          status={statusLabel}
          certificate_number={certificate_number || ""}
        />
      ) : (
        <RequestInfo
          request={request}
          status={statusLabel}
          queryText={queryText}
          isNonConsignmentCertificate={isNonConsignmentCertificate}
        />
      )}
    </Link>
  )

  const getHref = (isDraft: boolean) => {
    if (isDraft) {
      return !submissionLayout || submissionLayout === "CUSTOM"
        ? `/export-request/request/${requestId}`
        : `/request/export/${requestId}`
    }
    return !submissionLayout || submissionLayout === "CUSTOM"
      ? `/requests/${requestId}`
      : `/request/export/view/${requestId}`
  }

  const isDraft = status === ExportStatus.Draft
  const href = getHref(isDraft)

  if (isMobile) {
    return content(href)
  }

  return <TableCell key={`${index}-Request`}>{content(href)}</TableCell>
}

const getThreePointOneSwitch = ({
  status,
  index,
  request,
  requestId,
  isMobile = false,
  country_name,
  certificate_number,
  statusLabel,
  queryText,
  submissionLayout,
  onPrefetch,
  isNonConsignmentCertificate,
}: GetSwitchProps) => {
  const isConfigurable = submissionLayout && submissionLayout !== "CUSTOM"

  const content = (href: string) => (
    <Link
      href={href}
      custom
      prefetch={false}
      onPrefetch={isConfigurable ? onPrefetch : undefined}
    >
      {isMobile ? (
        <MobileRequestCell
          id={requestId}
          country_name={country_name || ""}
          request={request}
          status={statusLabel}
          certificate_number={certificate_number || ""}
        />
      ) : (
        <RequestInfo
          request={request}
          status={statusLabel}
          queryText={queryText}
          isNonConsignmentCertificate={isNonConsignmentCertificate}
        />
      )}
    </Link>
  )

  const getHref = (isDraft: boolean) => {
    if (isDraft) {
      return !submissionLayout || submissionLayout === "CUSTOM"
        ? `/export-request/request/${requestId}`
        : `/request/export/${requestId}`
    }
    return !submissionLayout || submissionLayout === "CUSTOM"
      ? `/requests/${requestId}`
      : `/request/export/view/${requestId}`
  }

  const isDraft =
    status === ExportStatus.Resubmit || status === ExportStatus.Draft
  const href = getHref(isDraft)

  if (isMobile) {
    return content(href)
  }

  return <TableCell key={`${index}-Request`}>{content(href)}</TableCell>
}

const getCertificateHref = (
  requestId: string,
  isDraft: boolean,
  submissionLayout?: string,
): string => {
  const isCustomLayout = !submissionLayout || submissionLayout === "CUSTOM"

  if (isDraft) {
    return isCustomLayout
      ? `/export-request/request/${requestId}`
      : `/request/export/${requestId}`
  }
  return isCustomLayout
    ? `/requests/${requestId}`
    : `/request/export/view/${requestId}`
}

export const getColumns = (
  isThreePointOneEnabled: boolean,
): ContentTableColumn[] => {
  return [
    {
      title: "Request",
      style: { width: "clamp(250px, 300px,  350px)", px: 3.5 },
      valueProp: "request_name",
      mobileColumn: true,
      customCell: (data: DataModel, index: number) => {
        const request = data["request_name" as keyof DataModel]
        const requestId = data["id" as keyof DataModel]
        const status = data["status" as keyof DataModel]
        const statusLabel = data["status_label" as keyof DataModel]

        if (!isThreePointOneEnabled) {
          return getThreePointZeroSwitch({
            status,
            index,
            request,
            requestId,
            statusLabel,
          })
        } else {
          return getThreePointOneSwitch({
            status,
            index,
            request,
            requestId,
            statusLabel,
          })
        }
      },
      mobileCustomCell: (data: DataModel, index: number) => {
        const request = data["request_name" as keyof DataModel]
        const requestId = data["id" as keyof DataModel]
        const status = data["status" as keyof DataModel]
        const country_name = data["country_name" as keyof DataModel]
        const certificate_number = data["certificate_number" as keyof DataModel]
        const statusLabel = data["status_label" as keyof DataModel]

        if (!isThreePointOneEnabled) {
          return getThreePointZeroSwitch({
            status,
            index,
            request,
            requestId,
            isMobile: true,
            country_name,
            certificate_number,
            statusLabel,
          })
        } else {
          return getThreePointOneSwitch({
            status,
            index,
            request,
            requestId,
            isMobile: true,
            country_name,
            certificate_number,
            statusLabel,
          })
        }
      },
    },
    {
      title: "Certificates",
      mobileColumn: true,
      style: { width: "clamp(200px, 250px,  350px)" },
      valueProp: "certificate_number",
      customCell: (data: DataModel, index: number) => (
        <TableCell key={`${index}-Certificates`}>
          <Certificate
            certificate_number={data["certificate_number" as keyof DataModel]}
            id={data.id}
          />
        </TableCell>
      ),
      mobileCustomCell: (data: DataModel, _index: number) => (
        <Stack key={data.id + "certificates"}>
          <Typography
            noWrap
            sx={{ lineHeight: 1.5 }}
            aria-label={`Last modified: ${toDate(data["modified_at" as keyof DataModel])}`}
          >
            {toDate(data["modified_at" as keyof DataModel])}
          </Typography>
        </Stack>
      ),
    },
    {
      title: "Destination",
      style: { width: "clamp(200px, 250px,  350px)" },
      valueProp: "destination_country_name",
    },
    {
      title: "Exporter",
      style: { width: "clamp(200px, 200px,  350px)" },
      valueProp: "organisation",
    },
    {
      title: "Departure date",
      style: { width: 200 },

      valueProp: "departure_ts",
      sort: {
        defaultSort: undefined,
        sortAttribute: "departure_ts",
        ascLabel: "Oldest to newest (Departure date)",
        descLabel: "Newest to oldest (Departure date)",
        default: true,
      },
      customCell: (data: DataModel, _index: number) => (
        <TableCell sx={{ textAlign: "left" }} key={data.id + "departure_ts"}>
          <Typography>{toDate(get(data, "departure_date"))}</Typography>
        </TableCell>
      ),
    },
    {
      title: "Last modified",
      style: { width: 200 },
      valueProp: "modified_ts",
      sort: {
        sortAttribute: "modified_ts",
        defaultSort: "desc",
        default: true,
        ascLabel: "Oldest to newest (Last modified)",
        descLabel: "Newest to oldest (Last modified)",
      },
      customCell: (data: DataModel, _index: number) => (
        <TableCell sx={{ textAlign: "left" }} key={data.id + "modified_ts"}>
          <Typography>{toDate(get(data, "modified_at"))}</Typography>
        </TableCell>
      ),
    },
  ]
}

export const getCell = (
  data: DataModel,
  field: string,
  index: number,
  queryText: string,
  style?: SxProps<Theme>,
) => (
  <TableCell key={`${index}-${field}`} sx={style}>
    <HighlightedText
      text={data[field as keyof DataModel]}
      highlight={queryText}
    />
  </TableCell>
)

export const getConfigColumns = (
  isThreePointOneEnabled: boolean,
  queryText: string,
  isSortDisabled = false,
  onPrefetchExport?: (requestId: string) => void,
): ContentTableColumn[] => {
  return [
    {
      title: "Request Name",
      style: { width: "clamp(250px, 300px,  350px)", px: 3.5 },
      valueProp: "request_name",
      mobileColumn: true,
      customCell: (data: DataModel, index: number) => {
        const request = data["request_name" as keyof DataModel]
        const requestId = data["id" as keyof DataModel]
        const status = data["status" as keyof DataModel]
        const statusLabel = data["status_label" as keyof DataModel]
        const submissionLayout = data["submission_layout" as keyof DataModel]
        const isNonConsignmentCertificate =
          data["certificate_type" as keyof DataModel] === "Nonconsignment"

        if (!isThreePointOneEnabled) {
          return getThreePointZeroSwitch({
            status,
            index,
            request,
            requestId,
            statusLabel,
            queryText,
            submissionLayout,
            onPrefetch: onPrefetchExport
              ? () => onPrefetchExport(requestId)
              : undefined,
            isNonConsignmentCertificate,
          })
        } else {
          return getThreePointOneSwitch({
            status,
            index,
            request,
            requestId,
            statusLabel,
            queryText,
            submissionLayout,
            onPrefetch: onPrefetchExport
              ? () => onPrefetchExport(requestId)
              : undefined,
            isNonConsignmentCertificate,
          })
        }
      },
      mobileCustomCell: (data: DataModel, index: number) => {
        const request = data["request_name" as keyof DataModel]
        const requestId = data["id" as keyof DataModel]
        const status = data["status" as keyof DataModel]
        const country_name = data["country_name" as keyof DataModel]
        const certificate_number = data["certificate_number" as keyof DataModel]
        const statusLabel = data["status_label" as keyof DataModel]
        const submissionLayout = data["submission_layout" as keyof DataModel]

        if (!isThreePointOneEnabled) {
          return getThreePointZeroSwitch({
            status,
            index,
            request,
            requestId,
            isMobile: true,
            country_name,
            certificate_number,
            statusLabel,
            submissionLayout,
            onPrefetch: onPrefetchExport
              ? () => onPrefetchExport(requestId)
              : undefined,
          })
        } else {
          return getThreePointOneSwitch({
            status,
            index,
            request,
            requestId,
            isMobile: true,
            country_name,
            certificate_number,
            statusLabel,
            submissionLayout,
            onPrefetch: onPrefetchExport
              ? () => onPrefetchExport(requestId)
              : undefined,
          })
        }
      },
    },
    {
      title: "Certificate Number",
      mobileColumn: true,
      style: { width: "clamp(200px, 250px,  350px)" },
      valueProp: "certificate_number",
      sort: !isSortDisabled
        ? {
            defaultSort: undefined,
            sortAttribute: "certificate_number_sort",
            ascLabel: "Oldest to newest (Certificate Number)",
            descLabel: "Newest to oldest (Certificate Number)",
            default: true,
          }
        : undefined,
      customCell: (data: DataModel, index: number) => {
        const requestId = data["id" as keyof DataModel]
        const status = data["status" as keyof DataModel]
        const submissionLayout = data["submission_layout" as keyof DataModel]
        const isDraft = isThreePointOneEnabled
          ? status === ExportStatus.Resubmit || status === ExportStatus.Draft
          : status === ExportStatus.Draft
        const href = getCertificateHref(requestId, isDraft, submissionLayout)

        return (
          <TableCell key={`${index}-Certificates`}>
            <Certificate
              certificate_number={data["certificate_number" as keyof DataModel]}
              id={data.id}
              queryText={queryText}
              href={href}
            />
          </TableCell>
        )
      },
      mobileCustomCell: (data: DataModel, _index: number) => (
        <Stack key={data.id + "certificates"}>
          <Typography
            noWrap
            sx={{ lineHeight: 1.5 }}
            aria-label={`Last modified: ${toPacificAucklandDateFormat(data["modified_ts" as keyof DataModel])}`}
          >
            {toPacificAucklandDateFormat(
              data["modified_ts" as keyof DataModel],
            )}
          </Typography>
        </Stack>
      ),
    },
    {
      title: "Status",
      style: { width: "clamp(200px, 250px,  350px)" },
      valueProp: "status_label",
      sort: !isSortDisabled
        ? {
            defaultSort: undefined,
            sortAttribute: "status_label",
            ascLabel: "Oldest to newest (Status)",
            descLabel: "Newest to oldest (Status)",
            default: true,
          }
        : undefined,
      customCell: (data: DataModel, index: number) =>
        getCell(data, "status_label", index, queryText),
    },
    {
      title: "Destination",
      style: { width: "clamp(200px, 250px,  350px)" },
      valueProp: "destination_country_name",
      sort: !isSortDisabled
        ? {
            defaultSort: undefined,
            sortAttribute: "country_name",
            ascLabel: "Oldest to newest (Country Name)",
            descLabel: "Newest to oldest (Country Name)",
            default: true,
          }
        : undefined,
      customCell: (data: DataModel, index: number) =>
        getCell(data, "destination_country_name", index, queryText),
    },
    {
      title: "Exporter",
      style: { width: "clamp(200px, 250px,  350px)" },
      valueProp: "organisation",
      sort: !isSortDisabled
        ? {
            defaultSort: undefined,
            sortAttribute: "organisation",
            ascLabel: "Oldest to newest (Country Name)",
            descLabel: "Newest to oldest (Country Name)",
            default: true,
          }
        : undefined,
      customCell: (data: DataModel, index: number) =>
        getCell(data, "organisation", index, queryText),
    },
    {
      title: "Template ID",
      style: { width: "clamp(200px, 250px,  350px)" },
      valueProp: "template_code",
      sort: !isSortDisabled
        ? {
            defaultSort: undefined,
            sortAttribute: "template_code",
            ascLabel: "Oldest to newest (Template ID)",
            descLabel: "Newest to oldest (Template ID)",
            default: true,
          }
        : undefined,
      customCell: (data: DataModel, index: number) =>
        getCell(data, "template_code", index, queryText),
    },
    {
      title: "Departure",
      style: { width: 200 },
      valueProp: "departure_ts",
      sort: !isSortDisabled
        ? {
            defaultSort: undefined,
            sortAttribute: "departure_ts",
            ascLabel: "Oldest to newest (Departure date)",
            descLabel: "Newest to oldest (Departure date)",
            default: true,
          }
        : undefined,
      customCell: (data: DataModel, _index: number) => (
        <TableCell sx={{ textAlign: "left" }} key={data.id + "departure_ts"}>
          <HighlightedText
            text={toPacificAucklandDateFormat(get(data, "departure_ts"))}
            highlight={queryText}
          />
        </TableCell>
      ),
    },
    {
      title: "Submitted",
      style: { width: 200 },
      valueProp: "submitted_at",
      sort: !isSortDisabled
        ? {
            sortAttribute: "submitted_at",
            defaultSort: undefined,
            default: true,
            ascLabel: "Oldest to newest (Last modified)",
            descLabel: "Newest to oldest (Last modified)",
          }
        : undefined,
      customCell: (data: DataModel, _index: number) => (
        <TableCell sx={{ textAlign: "left" }} key={data.id + "submitted_at"}>
          <HighlightedText
            text={toPacificAucklandDateFormat(get(data, "submitted_at"))}
            highlight={queryText}
          />
        </TableCell>
      ),
    },
    {
      title: "Modified",
      style: { width: 200 },
      valueProp: "modified_ts",
      sort: {
        sortAttribute: "modified_ts",
        defaultSort: undefined,
        default: true,
        ascLabel: "Oldest to newest (Last modified)",
        descLabel: "Newest to oldest (Last modified)",
      },
      customCell: (data: DataModel, _index: number) => (
        <TableCell sx={{ textAlign: "left" }} key={data.id + "modified_ts"}>
          <HighlightedText
            text={toPacificAucklandDateFormat(get(data, "modified_ts"))}
            highlight={queryText}
          />
        </TableCell>
      ),
    },
  ]
}

// const columns: ContentTableColumn[] = [
//   {
//     title: "Request",
//     style: { width: "clamp(250px, 300px,  350px)", px: 3.5 },
//     valueProp: "request_name",
//     mobileColumn: true,
//     customCell: (data: DataModel, index: number) => {
//       const request = data["request_name" as keyof DataModel]
//       const requestId = data["id" as keyof DataModel]
//       const status = data["status" as keyof DataModel]

//       switch (status) {
//         case ExportCertificateStatus.RequestedToResubmit:
//         case ExportCertificateStatus.Draft:
//           return (
//             <TableCell key={`${index}-Request`}>
//               <Link href={`/export-request/request/${requestId}`} custom>
//                 <RequestInfo request={request} status={status} />
//               </Link>
//             </TableCell>
//           )
//         case ExportCertificateStatus.Raised:
//         case ExportCertificateStatus.Revoked:
//         default:
//           return (
//             <TableCell key={`${index}-Request`}>
//               <Link href={`/requests/${requestId}`} custom>
//                 <RequestInfo request={request} status={status} />
//               </Link>
//             </TableCell>
//           )
//       }
//     },
//     mobileCustomCell: (data: DataModel, index: number) => {
//       const request = data["request_name" as keyof DataModel]
//       const requestId = data["id" as keyof DataModel]
//       const status = data["status" as keyof DataModel]
//       const country_name = data["country_name" as keyof DataModel]
//       const certificate_number = data["certificate_number" as keyof DataModel]

//       switch (status) {
//         case ExportCertificateStatus.RequestedToResubmit:
//         case ExportCertificateStatus.Draft:
//           return (
//             <Link
//               href={`/export-request/request/${requestId}`}
//               custom
//               key={`${index}-Request`}
//             >
//               <MobileRequestCell
//                 id={data.id}
//                 country_name={country_name}
//                 request={request}
//                 status={status}
//                 certificate_number={certificate_number}
//               />
//             </Link>
//           )
//         case ExportCertificateStatus.Raised:
//         case ExportCertificateStatus.Revoked:
//         default:
//           return (
//             <Link
//               key={`${index}-Request`}
//               href={`/requests/${requestId}`}
//               custom
//             >
//               <MobileRequestCell
//                 id={data.id}
//                 country_name={country_name}
//                 request={request}
//                 status={status}
//                 certificate_number={certificate_number}
//               />
//             </Link>
//           )
//       }
//     },
//   },
//   {
//     title: "Certificates",
//     mobileColumn: true,
//     style: { width: "clamp(200px, 250px,  350px)" },
//     valueProp: "certificate_number",
//     customCell: (data: DataModel, index: number) => (
//       <TableCell key={`${index}-Certificates`}>
//         <Certificate
//           certificate_number={data["certificate_number" as keyof DataModel]}
//           id={data.id}
//         />
//       </TableCell>
//     ),
//     mobileCustomCell: (data: DataModel, _index: number) => (
//       <Stack key={data.id + "certificates"}>
//         <Typography
//           noWrap
//           sx={{ lineHeight: 1.5 }}
//           aria-label={`Last modified: ${toDate(data["modified_at" as keyof DataModel])}`}
//         >
//           {toDate(data["modified_at" as keyof DataModel])}
//         </Typography>
//       </Stack>
//     ),
//   },
//   {
//     title: "Destination",
//     style: { width: "clamp(200px, 250px,  350px)" },
//     valueProp: "country_name",
//   },
//   {
//     title: "Organisation",
//     style: { width: "clamp(200px, 200px,  350px)" },
//     valueProp: "organisation",
//   },
//   {
//     title: "Departure date",
//     style: { width: 200 },

//     valueProp: "departure_ts",
//     sort: {
//       defaultSort: undefined,
//       sortAttribute: "departure_ts",
//       ascLabel: "Oldest to newest (Departure date)",
//       descLabel: "Newest to oldest (Departure date)",
//       default: true,
//     },
//     customCell: (data: DataModel, _index: number) => (
//       <TableCell sx={{ textAlign: "left" }} key={data.id + "departure_ts"}>
//         <Typography>{toDate(get(data, "departure_date"))}</Typography>
//       </TableCell>
//     ),
//   },
//   {
//     title: "Last modified",
//     style: { width: 200 },
//     valueProp: "modified_ts",
//     sort: {
//       sortAttribute: "modified_ts",
//       defaultSort: "desc",
//       default: true,
//       ascLabel: "Oldest to newest (Last modified)",
//       descLabel: "Newest to oldest (Last modified)",
//     },
//     customCell: (data: DataModel, _index: number) => (
//       <TableCell sx={{ textAlign: "left" }} key={data.id + "modified_ts"}>
//         <Typography>{toDate(get(data, "modified_at"))}</Typography>
//       </TableCell>
//     ),
//   },
// ]

interface ExportRequestsContentTableProps {
  queryText?: string
  isHomePage?: boolean
}

const TABLE_HEADERS = [
  "Request Name",
  "Certificate Number",
  "Status",
  "Destination",
  "Exporter",
  "Template ID",
  "Departure",
  "Submitted",
  "Modified",
]

const TableLoadingSkeleton = ({ rows = 5 }: { rows?: number }) => (
  <Table>
    <TableHead>
      <TableRow>
        {TABLE_HEADERS.map((header) => (
          <TableCell
            key={header}
            sx={{ display: { xs: "none", md: "table-cell" } }}
          >
            <Typography variant="subtitle2">{header}</Typography>
          </TableCell>
        ))}
        <TableCell sx={{ display: { xs: "table-cell", md: "none" } }}>
          <Typography variant="subtitle2">Request</Typography>
        </TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {Array.from({ length: rows }).map((_, index) => (
        <TableRow key={index}>
          {TABLE_HEADERS.map((header, colIndex) => (
            <TableCell
              key={header}
              sx={{ display: { xs: "none", md: "table-cell" } }}
            >
              <Skeleton
                variant="text"
                width={colIndex === 0 ? "90%" : "80%"}
                height={20}
              />
            </TableCell>
          ))}
          <TableCell sx={{ display: { xs: "table-cell", md: "none" } }}>
            <Stack direction="row" spacing={2}>
              <Skeleton variant="circular" width={40} height={40} />
              <Stack spacing={0.5} flex={1}>
                <Skeleton variant="text" width="70%" height={24} />
                <Skeleton variant="text" width="50%" height={18} />
              </Stack>
            </Stack>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
)

export const ExportRequestsContentTable = ({
  queryText = "",
  isHomePage = false,
}: ExportRequestsContentTableProps) => {
  const {
    requestsCount = 0,
    totalRequestsCount = 0,
    requests,
    paginationCount = 1,
    paginationDefaultPage = 1,
    onChangePagination,
    onSortBy,
    isLoading,
  } = useViewAllRequest()

  const { hasPermission, hasAccess } = usePermission()
  const isThreePointTwoEnabled = hasPermission([
    FEATURE_FLAGS.FEATURE_FLAG_CERTIFICATE_REPLACEMENT_V2,
  ])
  const isAppHytoEnabled = hasPermission([FEATURE_FLAGS.FEATURE_FLAG_APPHYTO])
  const isOCAUser = hasAccess([ROLE.OCABorder])

  const { prefetchExport } = usePrefetchRequest()

  const homePageProps = {
    totalNumber: 20,
    pageCount: 1,
    currentPage: 1,
    onSort: (_attr: string, _direction: "asc" | "desc") => {},
    onPageChange: () => {},
    onlyOnePage: true,
  }

  const allRequestsProps = {
    totalNumber: requestsCount,
    pageCount: paginationCount,
    currentPage: paginationDefaultPage,
    onSort: onSortBy,
    onPageChange: onChangePagination,
    Filter: isOCAUser ? undefined : <Filters />,
    exportData: (
      <Permission
        allow={[FEATURE_FLAGS.FEATURE_FLAG_EXPORT_CERTIFICATE_DOWNLOAD]}
      >
        <ExportRequestData />
      </Permission>
    ),
  }

  const tableProps = isHomePage ? homePageProps : allRequestsProps

  if (isLoading && requests.length === 0) {
    return <TableLoadingSkeleton rows={isHomePage ? 5 : 10} />
  }

  if (totalRequestsCount === 0) {
    return <EmptyPage />
  }

  const columns = isOCAUser
    ? getOcaColumns(queryText)
    : isAppHytoEnabled
      ? getConfigColumns(isThreePointTwoEnabled, queryText, isHomePage)
      : getColumns(isThreePointTwoEnabled)

  return <ContentTable data={requests} {...tableProps} columns={columns} />
}
